import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { addExistingSubs, inviteSubs } from "./subs.api";
import {
  fetchSubs,
  setPage,
  setPageSize,
  setName,
  setService,
  setSortKey,
  setStatus,
  removeSubs,
  setInviteOpen,
  sendSubsInvite,
} from "./subs.slice";
import { uuid } from "@/interfaces";
import { toast } from "sonner";
import type { SubcontractSortKey, SubcontractStatus } from "@/repositories/subs.repository";

export function useSubs() {
  const dispatch = useAppDispatch();
  const { subs, loading, total, filters, inviteSaving, inviteOpen, inviteError } = useAppSelector((s) => s.subs);

  useEffect(() => {
    dispatch(fetchSubs());
  }, [filters.page, filters.pageSize, filters.name, filters.service, filters.sortKey, filters.sortOrder, filters.status, dispatch]);

  const unlinkSubs = async (subsId: uuid) => {
    await dispatch(removeSubs(subsId));
    dispatch(fetchSubs());
  };

  const handleSubsSubmit = async (data: { type: "existing" | "invite"; organizationId?: string; email?: string }) => {
    try {
      if (data.type === "existing" && data.organizationId) {
        await addExistingSubs(data.organizationId);
        toast.success("Subs organization has been added successfully!");
        dispatch(fetchSubs());
      } else if (data.type === "invite" && data.email) {
        const res = await inviteSubs(data.email);
        if (res.alreadyOnboarded) {
          toast.success("Subs organization has been linked successfully!");
          dispatch(fetchSubs());
        } else {
          toast.success("Subs organization has been sent an invite!");
        }
      }

      openInviteModal(false);
    } catch (error: any) {
      throw error;
    }
  };

  const sendInvite = async (email: string) => {
    return handleSubsSubmit({ type: "invite", email });
  };

  const openInviteModal = (open: boolean) => dispatch(setInviteOpen(open));

  return {
    subs,
    loading,
    total,
    page: filters.page,
    pageSize: filters.pageSize,
    name: filters.name,
    service: filters.service,
    sortKey: filters.sortKey,
    sortOrder: filters.sortOrder,
    status: filters.status,
    setPage: (p: number) => dispatch(setPage(p)),
    setPageSize: (s: number) => dispatch(setPageSize(s)),
    setName: (n: string) => dispatch(setName(n)),
    setService: (svc: string) => dispatch(setService(svc)),
    setSortKey: (key: SubcontractSortKey) => dispatch(setSortKey(key)),
    setStatus: (status: SubcontractStatus) => dispatch(setStatus(status)),
    removeSubs: unlinkSubs,

    inviteOpen,
    inviteError,
    inviteSaving,
    openInviteModal,
    sendInvite,
    handleSubsSubmit,
  };
}
